import type { Layout, LayoutItem } from 'react-grid-layout';
import { packLayoutByReadingOrder, sortLayoutByReadingOrder } from './sourcePageLayout';

export interface ContinuationDropPlan {
  readonly fitsOnContinuation: boolean;
  /** Existing user blocks plus the dropped block, packed in reading order below the slice. */
  readonly userBlocks: Layout;
}

/**
 * Decides whether a dropped block fits on a continuation page in the free area below the
 * expanded block's slice, and returns the resulting user-block layout. Blocks are packed in
 * reading order across the full page width (like a regular page), so a half-width block lands
 * beside an existing half-width block when there is horizontal room, wrapping to a new row only
 * when the current row is full. `existingUserBlocks` are the previously-dropped user blocks
 * (without the static slice); their stored `y` offset is ignored and re-derived by the pack.
 */
export const planContinuationDrop = ({
  existingUserBlocks,
  movedItem,
  sliceRowCount,
  pageRows,
  noColumns,
}: {
  existingUserBlocks: Layout;
  movedItem: LayoutItem;
  sliceRowCount: number;
  pageRows: number;
  noColumns: number;
}): ContinuationDropPlan => {
  const packed = packLayoutByReadingOrder({
    items: [...sortLayoutByReadingOrder(existingUserBlocks), movedItem],
    noColumns,
  }).map((item) => ({ ...item, y: item.y + sliceRowCount }));
  const occupiedBottom = packed.reduce((max, item) => Math.max(max, item.y + item.h), sliceRowCount);
  return { fitsOnContinuation: occupiedBottom <= pageRows, userBlocks: packed };
};
